/* eslint-disable */

import * as THREE from 'three'
import PolygoneTexture from './PolygoneTexture.js'
import EventEmitter from './EventEmitter.js'

class Hexagones extends EventEmitter
{
    /**
     * Constructor
     */
    constructor(options = {})
    {
        super(options)

        // Options
        this.debug = typeof options.debug !== 'undefined' ? options.debug : false
        this.mapMaskImage = typeof options.mapMaskImage !== 'undefined' ? options.mapMaskImage : false

        // Container
        this.container = new THREE.Object3D()
        this.setTextures()

        this.setMapMask(() =>
        {
            this.setPixelsData()
            this.setPlanes()
            this.trigger('ready')
        })
    }

    /**
     * Set Map Mask
     */
    setMapMask(callback)
    {
        this.mapMask = {}
        this.mapMask.ratio = 1

        // Pick color
        this.mapMask.pickPixelData = (_x, _y) =>
        {
            const x = Math.floor(_x * this.mapMask.image.width)
            const y = Math.floor(_y * this.mapMask.image.height)
            const index = y * this.mapMask.image.width * 4 + x * 4
            const luminosity = (this.mapMask.imageData.data[index] + this.mapMask.imageData.data[index + 1] + this.mapMask.imageData.data[index + 2]) / 3 / 255
            const r = this.mapMask.imageData.data[index + 0] / 255
            const g = this.mapMask.imageData.data[index + 1] / 255
            const b = this.mapMask.imageData.data[index + 2] / 255
            const l = (r + g + b) / 3

            return {
                r: r,
                g: g,
                b: b,
                l: l
            }
        }

        // Canvas
        this.mapMask.canvas = document.createElement('canvas')

        // Debug
        if(this.debug)
        {
            this.mapMask.canvas.style.position = 'absolute'
            this.mapMask.canvas.style.top = '140px'
            this.mapMask.canvas.style.left = 0
            this.mapMask.canvas.style.zIndex = 1
            this.mapMask.canvas.style.backgroundColor = 'blue'
            document.body.appendChild(this.mapMask.canvas)
        }

        // Context
        this.mapMask.context = this.mapMask.canvas.getContext('2d')

        // Load image
        this.mapMask.image = new Image()
        this.mapMask.image.addEventListener('load', () =>
        {
            // Update image size
            this.mapMask.image.width = Math.floor(this.mapMask.image.width * this.mapMask.ratio)
            this.mapMask.image.height = Math.floor(this.mapMask.image.height * this.mapMask.ratio)

            // Update canvas
            this.mapMask.canvas.width = this.mapMask.image.width
            this.mapMask.canvas.height = this.mapMask.image.height

            // Draw image
            this.mapMask.context.drawImage(this.mapMask.image, 0, 0, this.mapMask.image.width, this.mapMask.image.height)

            // Get image data
            this.mapMask.imageData = this.mapMask.context.getImageData(0, 0, this.mapMask.image.width, this.mapMask.image.height)

            // Continue
            callback()
        })
        this.mapMask.image.src = this.mapMaskImage
    }

    /**
     * Set pixels data
     */
    setPixelsData()
    {
        this.pixelsData = []

        const radius = 2
        const steps = 220

        let i = 0

        while(i < 1)
        {
            const magic = Math.cos(i * Math.PI)
            const y = magic * radius * 0.5
            const stepRadius = Math.abs(Math.sin(i * Math.PI))
            const circumference =  Math.PI * stepRadius * 2
            const segments = Math.floor(circumference / 0.015)

            let j = 0

            while(j < 1)
            {
                const pixelData = this.mapMask.pickPixelData(1 - j, i)

                if(pixelData.l < 0.5)
                {
                    const x = Math.cos(Math.PI * j * 2) * stepRadius
                    const z = Math.sin(Math.PI * j * 2) * stepRadius

                    this.pixelsData.push({x, y, z, r: pixelData.r})
                }

                j += 1 / segments
            }

            i += 1 / steps
        }
    }

    /**
     * Set textures
     */
    setTextures()
    {
        // Red
        this.polygoneTextureRed = new PolygoneTexture({
            debug: false,
            resolution: 128,
            shapes:
            [
                {
                    edgesCount: 6,
                    radius: 52,
                    angle: Math.PI * 2 / 12,
                    strokeStyle: '#cb0f2e',
                    lineWidth: 16
                }
            ]
        })
        this.polygoneTextureRed.texture.needsUpdate = true

        // Gray
        this.polygoneTextureGray = new PolygoneTexture({
            debug: false,
            resolution: 128,
            shapes:
            [
                {
                    edgesCount: 6,
                    radius: 52,
                    angle: Math.PI * 2 / 12,
                    strokeStyle: '#442727',
                    lineWidth: 16
                }
            ]
        })
        this.polygoneTextureGray.texture.needsUpdate = true
    }

    /**
     * Set Planes
     */
    setPlanes()
    {
        const center = new THREE.Vector3()

        // Red geometry and material
        const polygoneMaterialRed = new THREE.MeshBasicMaterial({ map: this.polygoneTextureRed.texture, side: THREE.DoubleSide, transparent: true})
        polygoneMaterialRed.depthTest = false
        const combinedGeometryRed = new THREE.Geometry()

        // Gray geometry and material
        const polygoneMaterialGray = new THREE.MeshBasicMaterial({ map: this.polygoneTextureGray.texture, side: THREE.DoubleSide, transparent: true})
        polygoneMaterialGray.depthTest = false
        const combinedGeometryGray = new THREE.Geometry()

        // Each coordinate
        for(let i = 0; i < this.pixelsData.length; i++)
        {
            // Set up
            const _pixelData = this.pixelsData[i]

            if(_pixelData.r > 0.8)
            {
                _pixelData.r = 1
            }

            // Red
            if(_pixelData.r > 0.3)
            {
                const sizeRed = 0.014 * _pixelData.r
                const planeGeometryRed = new THREE.PlaneGeometry(sizeRed, sizeRed, 1, 1)
                const meshRed = new THREE.Mesh(planeGeometryRed, polygoneMaterialRed)

                meshRed.position.set(_pixelData.x, _pixelData.y, _pixelData.z)
                meshRed.lookAt(center)

                meshRed.updateMatrix()
                combinedGeometryRed.merge(meshRed.geometry, meshRed.matrix)
            }

            // Gray
            if(_pixelData.r <= 0.8)
            {
                const sizeGray = 0.014
                const planeGeometryGray = new THREE.PlaneGeometry(sizeGray, sizeGray, 1, 1)
                const meshGray = new THREE.Mesh(planeGeometryGray, polygoneMaterialGray)

                meshGray.position.set(_pixelData.x, _pixelData.y, _pixelData.z)
                meshGray.lookAt(center)

                meshGray.updateMatrix()
                combinedGeometryGray.merge(meshGray.geometry, meshGray.matrix)
            }
        }

        // Add gray
        const combinedMeshGray = new THREE.Mesh(combinedGeometryGray, polygoneMaterialGray)
        this.container.add(combinedMeshGray)

        // Add red
        const combinedMeshRed = new THREE.Mesh(combinedGeometryRed, polygoneMaterialRed)
        this.container.add(combinedMeshRed)
    }
}

export default Hexagones



// WEBPACK FOOTER //
// ./src/scripts/components/page-home/Background/Hexagones.js